import React from 'react'
import { Button } from './ui'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu'
import { useLocalization } from '../hooks/useLocalization'
import { useOrganizationCurrency } from '../hooks/useOrganizationCurrency'
import { useOrgsApi } from 'src/api/db/apiOrgs'
import { useOrgUsersApi } from 'src/api/db/apiOrgUsers'
import { Row } from './Semantic/all'

// Swedish Flag Component
const SwedishFlag = ({ className = 'w-5 h-4' }: { className?: string }) => (
  <div
    className={`${className} border border-gray-300 rounded-sm overflow-hidden`}
  >
    <div className="w-full h-full bg-blue-600 relative">
      <div className="absolute inset-y-0 left-1/3 w-1/6 bg-yellow-400"></div>
      <div className="absolute inset-x-0 top-1/3 h-1/6 bg-yellow-400"></div>
    </div>
  </div>
)

// British Flag Component
const BritishFlag = ({ className = 'w-5 h-4' }: { className?: string }) => (
  <div
    className={`${className} border border-gray-300 rounded-sm overflow-hidden`}
  >
    <img
      src="https://flagcdn.com/gb.svg"
      alt="United Kingdom flag"
      className="w-full h-full object-cover"
      onError={(e) => {
        // Fallback to a simple red, white, blue representation if image fails to load
        e.currentTarget.style.display = 'none'
        // @ts-ignore
        e.currentTarget.nextElementSibling!.style.display = 'block'
      }}
    />
    <div
      className="w-full h-full hidden"
      style={{
        background:
          'linear-gradient(to bottom, #b22234 0%, #b22234 14.28%, #ffffff 14.28%, #ffffff 28.56%, #b22234 28.56%, #b22234 42.84%, #ffffff 42.84%, #ffffff 57.12%, #b22234 57.12%, #b22234 71.4%, #ffffff 71.4%, #ffffff 85.68%, #b22234 85.68%, #b22234 100%)',
      }}
    >
      <div
        className="absolute top-0 left-0 w-2/5 h-3/5 bg-blue-600"
        style={{
          background: '#3c3b6e',
        }}
      ></div>
    </div>
  </div>
)

interface LanguageOption {
  code: string
  name: string
  flag: React.ReactNode
}

const languages: LanguageOption[] = [
  {
    code: 'en',
    name: 'English',
    flag: <BritishFlag />,
  },
  {
    code: 'sv',
    name: 'Svenska',
    flag: <SwedishFlag />,
  },
]

interface CurrencyOption {
  code: 'SEK' | 'EUR'
  label: string
}

const currencies: CurrencyOption[] = [
  { code: 'SEK', label: 'SEK (kr)' },
  { code: 'EUR', label: 'EUR (€)' },
]

interface LanguageSelectorProps {
  variant?: 'light' | 'dark'
}

export const LanguageSelector = ({
  variant = 'light',
}: LanguageSelectorProps) => {
  const { locale, setLanguage } = useLocalization()
  const currency = useOrganizationCurrency()
  const { orgUser } = useOrgUsersApi()
  const { updateOrganization } = useOrgsApi({})
  const isDark = variant === 'dark'

  const currentLanguage =
    languages.find((lang) => lang.code === locale) || languages[0]
  const orgId = orgUser?.data?.organization_id

  const handleLanguageChange = (languageCode: string) => {
    setLanguage(languageCode as 'en' | 'sv')
    window.location.reload()
  }

  const handleCurrencyChange = (currencyCode: 'SEK' | 'EUR') => {
    if (!orgId || currencyCode === currency) return
    updateOrganization.mutate({ id: orgId, currency: currencyCode })
  }

  return (
    <Row>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className={`flex items-center gap-2 px-2 ${
              isDark ? 'hover:bg-neutral-800' : ''
            }`}
          >
            {currentLanguage.flag}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[120px]">
          {languages.map((language) => (
            <DropdownMenuItem
              key={language.code}
              onClick={() => handleLanguageChange(language.code)}
              className="flex items-center gap-2 cursor-pointer"
            >
              {language.flag}
              {language.name}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            disabled={!orgId || updateOrganization.isPending}
            className={`flex items-center gap-2 px-2 ${
              isDark ? 'hover:bg-neutral-800' : ''
            }`}
          >
            {currency}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[120px]">
          {currencies.map((curr) => (
            <DropdownMenuItem
              key={curr.code}
              onClick={() => handleCurrencyChange(curr.code)}
              disabled={curr.code === currency}
              className="flex items-center gap-2 cursor-pointer"
            >
              {curr.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </Row>
  )
}
